<template>
  <div :class="embedded ? 'mcp-access-embedded' : 'mcp-access-page'">
    <div class="mcp-access">
      <header class="hero">
        <div class="hero-kicker">
          <span class="dot" />
          <span>{{ cfg.tenantLabel }} · {{ cfg.envLabel }}</span>
        </div>
        <h1>用自己的身份，在智能体里查 Facebook 投放</h1>
        <p class="lede">
          支持 MCP 的智能体都能接（Cursor、Codex、Kiro 等只是常见例子）。按下面四步做就行。别人拿不到你的账户。改预算、改状态要另接变更配置，客户端里不要勾自动批准。
        </p>
      </header>

      <section class="tutorial">
        <div class="tutorial-head">
          <h2>视频教程</h2>
          <p>看文字不清楚时，按这段录屏跟着装一遍即可。申请入口、贴 Key 的步骤与调户中心 MCP 相同。</p>
        </div>
        <div class="tutorial-player">
          <video :src="cfg.tutorialVideoUrl" controls preload="metadata" playsinline>
            您的浏览器不支持视频播放，
            <a :href="cfg.tutorialVideoUrl" target="_blank" rel="noopener noreferrer">点此打开教程</a>
          </video>
        </div>
      </section>

      <ol class="rail" aria-label="接入步骤">
        <li class="rail-step">
          <span class="rail-index">1</span>
          <div>
            <strong>申请接口权限</strong>
            <p>先勾查询（中级，提交就能过）</p>
          </div>
        </li>
        <li class="rail-step">
          <span class="rail-index">2</span>
          <div>
            <strong>复制个人 Key</strong>
            <p>只属于你，用来认人</p>
          </div>
        </li>
        <li class="rail-step">
          <span class="rail-index">3</span>
          <div>
            <strong>贴进智能体</strong>
            <p>复制本页配置，连通即可查</p>
          </div>
        </li>
        <li class="rail-step">
          <span class="rail-index">4</span>
          <div>
            <strong>需要改数再接变更</strong>
            <p>会改线上投放，先预览再确认</p>
          </div>
        </li>
      </ol>

      <section class="step-card">
        <div class="step-card-head">
          <span class="step-num">1</span>
          <div>
            <h2>申请接口权限</h2>
            <p class="step-sub">
              没有权限，后面复制了 Key 也调不通。申请在中台完成，本页不能开通。先申请<strong>查询</strong>；要改状态、预算、出价、名称或发布草稿时，再申请<strong>变更</strong>。
            </p>
          </div>
        </div>

        <div class="level-tips">
          <div class="level-tip level-tip-ok">
            <strong>中级</strong>
            <span>查询和变更都是中级，提交后直接通过</span>
          </div>
          <div class="level-tip level-tip-warn">
            <strong>变更会改线上数据</strong>
            <span>先预览，再确认执行；不要开自动批准</span>
          </div>
        </div>

        <div class="api-panel">
          <div class="api-panel-title">
            <span class="badge badge-ok">中级 · 当天能过</span>
            <h3>查询 {{ cfg.query.name }}（先申请）</h3>
          </div>
          <ul class="api-list">
            <li v-for="name in cfg.query.apis" :key="name">{{ name }}</li>
          </ul>
          <div class="step-actions">
            <a class="btn btn-solid" :href="cfg.applyUrl" target="_blank" rel="noopener noreferrer">去申请这些接口</a>
            <button type="button" class="btn btn-ghost" @click="copyNames(cfg.query.apis)">复制能力清单</button>
          </div>
        </div>
        <p class="lane-foot">
          在中台搜「{{ cfg.query.name }}」。勾选后提交。不要进开发用的接口管理页。
        </p>
      </section>

      <section class="step-card">
        <div class="step-card-head">
          <span class="step-num">2</span>
          <div>
            <h2>复制个人 Key</h2>
            <p class="step-sub">权限过了再来拿 Key。这是你的身份凭证，不要发给别人。</p>
          </div>
        </div>
        <div class="key-panel">
          <p>
            打开中台「个人 Key」页，点复制。配置里把 <code>{{ keyPlaceholder }}</code> 换成这一串即可。
          </p>
          <div class="step-actions">
            <a class="btn btn-solid" :href="cfg.keyUrl" target="_blank" rel="noopener noreferrer">打开个人 Key</a>
          </div>
        </div>
      </section>

      <section class="step-card">
        <div class="step-card-head">
          <span class="step-num">3</span>
          <div>
            <h2>贴进智能体</h2>
            <p class="step-sub">
              默认先接<strong>查询</strong>。把下面配置贴进 MCP 客户端，再把
              <code>{{ keyPlaceholder }}</code> 换成第 2 步复制的内容。要改数时，等变更权限通过后再贴「变更」或「查询+变更」。
            </p>
          </div>
        </div>

        <div class="studio">
          <div class="studio-bar">
            <div class="tabs" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                class="tab"
                :class="{ on: client === tab.id }"
                @click="client = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>
            <button type="button" class="btn btn-solid" @click="copySnippet">复制配置</button>
          </div>
          <p class="studio-hint">
            Cursor 类客户端贴进 <code>mcp.json</code>。客户端里<strong>不要</strong>勾自动批准。不要把 Key 提交到 git。
          </p>
          <pre class="sheet" tabindex="0"><code>{{ snippet }}</code></pre>
        </div>
      </section>

      <section class="step-card step-card-muted">
        <div class="step-card-head">
          <span class="step-num step-num-warn">4</span>
          <div>
            <h2>要用变更时，再申请变更接口</h2>
            <p class="step-sub">
              会改线上投放数据（启停、预算、出价、名称、发布草稿）。通过后再把「查询+变更」配置贴进客户端。每次改数都要先预览，再确认。
            </p>
          </div>
        </div>
        <div class="api-panel api-panel-warn">
          <div class="api-panel-title">
            <span class="badge badge-warn">变更 · 会改线上投放</span>
            <h3>变更 {{ cfg.mutate.name }}（查询通了再申请）</h3>
          </div>
          <ul class="api-list">
            <li v-for="name in cfg.mutate.apis" :key="name">{{ name }}</li>
          </ul>
          <p class="lane-foot">客户端里先预览，再确认执行；不要开自动批准。</p>
          <div class="step-actions">
            <a class="btn btn-ghost" :href="cfg.applyUrl" target="_blank" rel="noopener noreferrer">去申请变更接口</a>
            <button type="button" class="btn btn-ghost" @click="copyNames(cfg.mutate.apis)">复制能力清单</button>
          </div>
        </div>
      </section>

      <footer class="notes">
        <p>查询、变更都是中级接口，申请后直接通过。</p>
        <p>Key 只代表你本人。别人用你的 Key，看到的就是你的户。</p>
        <p>本页地址已按 {{ cfg.tenantLabel }} 正式环境写好。</p>
      </footer>
    </div>
  </div>
</template>

<script>
const KEY_PLACEHOLDER = '您的个人key';

const MCP_ACCESS_CONFIG = {
  tenantLabel: '吉客印',
  envLabel: '正式',
  keyUrl: 'https://manage.giikin.com/fe/acore/auth-ai/user-api',
  applyUrl: 'https://manage.giikin.com/fe/acore/auth-ai/user-api',
  tutorialVideoUrl: 'https://sucai.giikin.cn/material/mcp-docs/mcp-access-tutorial-1789634879426.mp4',
  query: {
    name: 'giikin-fb-query',
    url: 'https://ai-gateway.giikin.com/mcp-servers/giikin-fb-query',
    apis: ['查询凭证', '全局搜索', '按账户反查凭证', '查询广告账户', '查询系列 / 广告组 / 广告', '查询报表汇总', '查询草稿'],
  },
  mutate: {
    name: 'giikin-fb-mutate',
    url: 'https://ai-gateway.giikin.com/mcp-servers/giikin-fb-mutate',
    apis: ['变更预览', '改状态', '改预算', '改名称', '改出价', '发布草稿'],
  },
};

function serverEntry(url) {
  return {
    url,
    headers: { Authorization: `Bearer ${KEY_PLACEHOLDER}` },
  };
}

function cursorJson(servers) {
  return JSON.stringify({ mcpServers: servers }, null, 2);
}

export default {
  name: 'McpAccess',
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cfg: MCP_ACCESS_CONFIG,
      keyPlaceholder: KEY_PLACEHOLDER,
      client: 'query',
      tabs: [
        { id: 'query', label: '查询' },
        { id: 'mutate', label: '变更' },
        { id: 'both', label: '查询+变更' },
      ],
    };
  },
  computed: {
    snippet() {
      const { query, mutate } = MCP_ACCESS_CONFIG;
      if (this.client === 'mutate') {
        return cursorJson({ [mutate.name]: serverEntry(mutate.url) });
      }
      if (this.client === 'both') {
        return cursorJson({
          [query.name]: serverEntry(query.url),
          [mutate.name]: serverEntry(mutate.url),
        });
      }
      return cursorJson({ [query.name]: serverEntry(query.url) });
    },
  },
  methods: {
    copySnippet() {
      this.copyText(this.snippet, '已复制，粘贴到客户端后刷新 MCP');
    },
    copyNames(list) {
      this.copyText(list.join('\n'), '已复制能力清单');
    },
    copyText(text, okMsg) {
      const done = () => this.$message.success(okMsg);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
        return;
      }
      this.fallbackCopy(text, done);
    },
    fallbackCopy(text, done) {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', 'readonly');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        done();
      } catch (e) {
        this.$message.error('复制失败，请手动选中');
      }
      document.body.removeChild(el);
    },
  },
};
</script>

<style scoped>
.mcp-access-page {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px 32px;
}

.mcp-access-embedded {
  background: transparent;
}

.mcp-access {
  --ink: #0f172a;
  --mute: #64748b;
  --line: #e2e8f0;
  --paper: #f8fafc;
  --card: #ffffff;
  --query: #0f766e;
  --query-soft: #ccfbf1;
  --warn: #9f1239;
  --warn-soft: #ffe4e6;
  --sheet: #0f172a;
  --sheet-fg: #e2e8f0;
  --accent: #1d4ed8;
  max-width: 960px;
  color: var(--ink);
}

.hero {
  margin-bottom: 20px;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--mute);
  margin-bottom: 10px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--query);
  box-shadow: 0 0 0 4px var(--query-soft);
}

h1 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.lede {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: var(--mute);
}

.tutorial {
  margin: 0 0 20px;
  padding: 16px 16px 14px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
}

.tutorial-head h2 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 650;
}

.tutorial-head p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--mute);
}

.tutorial-player {
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: #0f172a;
  border: 1px solid var(--line);
}

.tutorial-player video {
  display: block;
  width: 100%;
  max-height: 420px;
  background: #0f172a;
}

.rail {
  list-style: none;
  margin: 0 0 20px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.rail-step {
  display: flex;
  gap: 8px;
  padding: 12px 10px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 12px;
}

.rail-index {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  font-size: 12px;
  font-weight: 650;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rail-step strong {
  display: block;
  font-size: 13px;
  margin-bottom: 2px;
}

.rail-step p {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--mute);
}

.step-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 12px;
}

.step-card-muted {
  background: var(--paper);
}

.step-card-head {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}

.step-num {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: var(--query);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-num-warn {
  background: var(--warn);
}

.step-card-head h2 {
  margin: 0 0 4px;
  font-size: 16px;
}

.step-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--mute);
}

.level-tips {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.level-tip {
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.level-tip-ok {
  background: var(--query-soft);
  color: #115e59;
}

.level-tip-warn {
  background: var(--warn-soft);
  color: #9f1239;
}

.api-panel,
.key-panel {
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 14px;
}

.api-panel-warn {
  background: #fff;
  border-color: #fecdd3;
}

.api-panel-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.api-panel-title h3 {
  margin: 0;
  font-size: 14px;
}

.badge {
  font-size: 11px;
  font-weight: 650;
  padding: 3px 8px;
  border-radius: 999px;
}

.badge-ok {
  color: var(--query);
  background: var(--query-soft);
}

.badge-warn {
  color: var(--warn);
  background: var(--warn-soft);
}

.api-list {
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
  max-height: 220px;
  overflow: auto;
}

.api-list li {
  font-size: 12px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--line);
}

.api-list li:last-child {
  border-bottom: 0;
}

.key-panel p,
.lane-foot {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.65;
  color: var(--mute);
}

.lane-foot {
  margin-top: 12px;
  margin-bottom: 0;
}

.key-panel code,
.step-sub code,
.studio-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  background: #fff;
  border: 1px solid var(--line);
  padding: 1px 5px;
  border-radius: 4px;
}

.step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  background: transparent;
}

.btn-ghost {
  color: var(--ink);
  background: #fff;
  border-color: var(--line);
}

.btn-solid {
  color: #fff;
  background: var(--accent);
  border-color: var(--accent);
}

.studio {
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.studio-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tab {
  height: 30px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  font-size: 12px;
  color: var(--mute);
  cursor: pointer;
}

.tab.on {
  background: var(--ink);
  color: #fff;
}

.studio-hint {
  margin: 0;
  padding: 10px 14px 0;
  font-size: 12px;
  color: var(--mute);
  line-height: 1.5;
}

.sheet {
  margin: 12px;
  padding: 14px 16px;
  background: var(--sheet);
  color: var(--sheet-fg);
  border-radius: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.65;
  overflow: auto;
  white-space: pre-wrap;
}

.notes p {
  margin: 0 0 4px;
  font-size: 12px;
  color: var(--mute);
  line-height: 1.55;
}

@media (max-width: 900px) {
  .rail,
  .level-tips {
    grid-template-columns: 1fr;
  }
}
</style>
